import React, { useState, useEffect } from 'react';
import { Tooltip, Box, Button, IconButton } from '@mui/material';
import { useThemeColors } from '../../../utils/useThemeColors';

const LargeFloatingNavMenu = ({ onNavigate, showOnlySelected, toggleShowOnlySelected, selectedSectionId, disabledSections = {}, hiddenSections = [], onToggleSize, onDockTop }) => {
  const [position, setPosition] = useState({ x: 27, y: Math.round(window.innerHeight / 2) - 150 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const menuItems = [
    { id: 'Configuration', label: 'Configuration', tooltip: 'Window Configuration' },
    { id: 'SizingAndFrameTop', label: 'Sizing & Frame - Top', tooltip: 'Sizing & Frame - Top Row' },
    { id: 'GrillsTop', label: 'Grills - Top', tooltip: 'Grills - Top Row' },
    { id: 'SizingAndFrame', label: 'Sizing & Frame', tooltip: 'Sizing & Frame - Main Row' },
    { id: 'Grills', label: 'Grills', tooltip: 'Grills - Main Row' },
    { id: 'Glass', label: 'Glass', tooltip: 'Glass' },
    { id: 'Finish', label: 'Finish', tooltip: 'Finish' },
    { id: 'AdditionalItems', label: 'Additional Items', tooltip: 'Additional Items' },
    { id: 'Installation', label: 'Installation', tooltip: 'Installation' },
    { id: 'Notes', label: 'Notes', tooltip: 'Notes' },
    { id: 'Discounts', label: 'Quantity & Discounts', tooltip: 'Quantity & Discounts' },
    { id: 'top', label: 'Go to Top', tooltip: 'Go to Top' },
    { id: 'toggleView', label: showOnlySelected ? 'Show All' : 'Show Selected', tooltip: showOnlySelected ? 'Show All Sections' : 'Show Selected Only', isToggle: true },
  ];

  const tc = useThemeColors();

  const handleMouseDown = (e) => {
    if (e.target.closest('.nav-button')) return;
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragOffset.x,
      y: e.clientY - dragOffset.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleClick = (id) => {
    if (disabledSections[id]) return;
    if (id === 'top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (id === 'toggleView') {
      toggleShowOnlySelected();
    } else {
      onNavigate(id);
    }
  };

  return (
    <Box
      onMouseDown={handleMouseDown}
      sx={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        zIndex: 9999,
        backgroundColor: tc.panelBg,
        border: `1px solid ${tc.panelBorder}`,
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        padding: '4px',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        width: 'fit-content',
      }}
    >
      {/* Toggle size arrow */}
      <Tooltip
        title="Collapse menu"
        arrow
        placement="right"
        disableInteractive
        slotProps={{
          popper: {
            sx: { zIndex: 10000, pointerEvents: 'none' }
          }
        }}
      >
        <IconButton
          className="nav-button"
          onClick={onToggleSize}
          sx={{
            width: '100%',
            height: 16,
            fontSize: '10px',
            padding: 0,
            borderRadius: '4px',
            backgroundColor: tc.headerBg,
            '&:hover': {
              backgroundColor: tc.headerHover,
            },
          }}
        >
          ▲
        </IconButton>
      </Tooltip>
      {onDockTop && (
        <Tooltip
          title="Dock as top bar"
          arrow
          placement="right"
          disableInteractive
          slotProps={{ popper: { sx: { zIndex: 10000, pointerEvents: 'none' } } }}
        >
          <IconButton
            className="nav-button"
            onClick={onDockTop}
            sx={{
              width: '100%',
              height: 16,
              fontSize: '10px',
              padding: 0,
              borderRadius: '4px',
              backgroundColor: tc.headerBg,
              '&:hover': { backgroundColor: tc.headerHover },
            }}
          >
            ⤓
          </IconButton>
        </Tooltip>
      )}
      {menuItems.filter(item => !hiddenSections.includes(item.id)).map((item) => (
        <Tooltip
          key={item.id}
          title={item.tooltip}
          arrow
          placement="right"
          disableInteractive
          slotProps={{
            popper: {
              sx: { zIndex: 10000, pointerEvents: 'none' }
            }
          }}
        >
          <Button
            className="nav-button"
            onClick={() => handleClick(item.id)}
            disabled={disabledSections[item.id]}
            sx={{
              fontSize: '11px',
              padding: '2px 8px',
              minHeight: '24px',
              textTransform: 'none',
              justifyContent: 'flex-start',
              borderRadius: '4px',
              backgroundColor: disabledSections[item.id]
                ? tc.buttonBgDisabled
                : item.isToggle
                  ? (showOnlySelected ? tc.buttonBgToggleOn : tc.buttonBgToggleOff)
                  : (showOnlySelected && selectedSectionId === item.id ? tc.buttonBgSelected : tc.buttonBg),
              color: disabledSections[item.id] ? tc.textDisabled : tc.text,
              opacity: disabledSections[item.id] ? 0.4 : 1,
              cursor: disabledSections[item.id] ? 'not-allowed' : 'pointer',
              '&:hover': {
                backgroundColor: disabledSections[item.id]
                  ? tc.buttonHoverDisabled
                  : item.isToggle
                    ? (showOnlySelected ? tc.buttonHoverActive : tc.buttonHover)
                    : tc.buttonHover,
              },
            }}
          >
            {item.label}
          </Button>
        </Tooltip>
      ))}
    </Box>
  );
};

export default LargeFloatingNavMenu;
